import {Text} from '@mantine/core';
import React from 'react';

import {
  LABELING_STRATEGIES_DISCARDING_NAME,
  LABELING_STRATEGY_DISCARD_NAME_TEXT,
  LABELING_STRATEGY_INPUT_LABEL,
  LABELING_STRATEGY_KEEP_NAME_AS_ALIAS_TEXT,
  LABELING_STRATEGY_TO_TEXT,
  LabelingStrategy,
  RegistryLabelingStrategy,
} from 'constants/RegistryConstants';
import preferences from 'Preferences';
import FormGroup from 'ui-library/bs/FormGroup';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';

import * as LabelingStrategiesUtil from './LabelingStrategiesUtil';

function LabelingStrategyDropdownOrMessage({
  disabled,
  labelingStrategies,
  onChange,
  placeholder,
  selectedLabelingStrategy,
}: {
  disabled: boolean;
  labelingStrategies: Array<LabelingStrategy> | null;
  onChange: (labelingStrategy: LabelingStrategy) => void;
  placeholder: string;
  selectedLabelingStrategy?: LabelingStrategy | null;
}): JSX.Element {
  // eslint-disable-next-line benchlint/no-eqeqeq-null
  if (labelingStrategies !== null && labelingStrategies.length === 0) {
    return <p className="help-block">No available options. Please select a schema.</p>;
  }
  if (
    // eslint-disable-next-line benchlint/no-eqeqeq-null
    labelingStrategies !== null &&
    labelingStrategies.length === 1 &&
    labelingStrategies[0] === selectedLabelingStrategy
  ) {
    return <p className="help-block">{LABELING_STRATEGY_TO_TEXT[selectedLabelingStrategy]}</p>;
  }

  const displayName =
    selectedLabelingStrategy != null ? LABELING_STRATEGY_TO_TEXT[selectedLabelingStrategy] : placeholder;

  const labelingStrategyOptions = (labelingStrategies || []).map((labelingStrategy) => (
    <Option value={labelingStrategy} onSelect={onChange} key={labelingStrategy}>
      {LABELING_STRATEGY_TO_TEXT[labelingStrategy]}
    </Option>
  ));

  return (
    <SelectDropdown
      disabled={disabled}
      displayName={displayName}
      menu={
        <Menu
          scrollable={true}
          height={Menu.HEIGHTS.MEDIUM}
          width={Menu.WIDTHS.FULL}
          value={selectedLabelingStrategy}
        >
          {labelingStrategyOptions}
        </Menu>
      }
    />
  );
}

function LabelingStrategyAliasHelperText({
  labelingStrategy,
}: {
  labelingStrategy?: LabelingStrategy | null;
}): JSX.Element | null {
  if (labelingStrategy === RegistryLabelingStrategy.SET_FROM_NAME_PARTS) {
    return (
      <Text color="dimmed" size="sm" fs="italic" mt="xs">
        {LABELING_STRATEGY_KEEP_NAME_AS_ALIAS_TEXT}
      </Text>
    );
  }
  if (
    labelingStrategy &&
    LABELING_STRATEGIES_DISCARDING_NAME.includes(labelingStrategy as RegistryLabelingStrategy)
  ) {
    return (
      <Text color="dimmed" size="sm" fs="italic" mt="xs">
        {LABELING_STRATEGY_DISCARD_NAME_TEXT}
      </Text>
    );
  }
  return null;
}

export interface LabelingStrategyInputProps {
  disabled?: boolean;
  label?: string;
  labelingStrategies: Array<LabelingStrategy> | null;
  labelingStrategy?: LabelingStrategy | null;
  labelStyle?: string;
  onChange: (labelingStrategy: LabelingStrategy) => void;
  /** Shown in the dropdown when no labeling strategy is selected */
  placeholder?: string;
  tagSchemaIds?: Array<string>;
}

function LabelingStrategyInput({
  tagSchemaIds,
  onChange,
  disabled = false,
  label = LABELING_STRATEGY_INPUT_LABEL,
  labelStyle,
  labelingStrategies,
  labelingStrategy,
  placeholder = 'Select a setting...',
}: LabelingStrategyInputProps): JSX.Element {
  const handleLabelingStrategyChange = (newLabelingStrategy: LabelingStrategy): void => {
    if (tagSchemaIds) {
      preferences.set(LabelingStrategiesUtil.getLabelingStrategyPrefKey(tagSchemaIds), newLabelingStrategy);
    }
    onChange(newLabelingStrategy);
  };

  return (
    <FormGroup>
      <FieldLabel label={label} labelStyle={labelStyle} isRequired={true} />
      <LabelingStrategyDropdownOrMessage
        disabled={Boolean(disabled)}
        labelingStrategies={labelingStrategies}
        onChange={handleLabelingStrategyChange}
        placeholder={placeholder}
        selectedLabelingStrategy={labelingStrategy}
      />
      <LabelingStrategyAliasHelperText labelingStrategy={labelingStrategy} />
    </FormGroup>
  );
}

export default LabelingStrategyInput;
